import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk-service';
import { Country } from '~/Api/Country';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	ErrorResponse,
	List,
	Product as ProductGroupCzReimbursementProduct,
	ProductUpdate,
} from '~/Api/User/ProductGroup/CzReimbursement';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import { Action } from '~/HrMaxPrice/Action';
import {
	GroupTypes,
	Module,
	pruneReferenceBasketVersions,
	ReferenceBasketVersion,
	resolveProductGroup,
	selectReferenceBasketVersions,
} from '~/PriceReferencing';
import { isSuklReferenceProduct, PricedProduct, Product as GeneralProduct } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices } from '~/ProductPrice';
import { State, ThunkAction } from '~/Store';

import {
	commitCzReimbursementUpdate,
	fetchCzReimbursement,
	fetchReferenceBasketVersions,
	handleCommitError,
	handleCzReimbursementUpdateResponse,
	pruneCzReimbursement,
	scheduleCzReimbursementUpdate,
} from './actions';
import CzReimbursement from './CzReimbursement';
import { ProductRow } from './ProductsTable/ProductsTable';
import { selectCzReimbursement, selectExport } from './reducer';

const groupProducts = memoizeOne(
	(czReimbursement: List | null, versions: ReferenceBasketVersion[] | null, showAll: boolean) => {
		const version =
			versions && czReimbursement
				? versions.find((version) => version.id === czReimbursement.finalCalculation.referenceBasketVersion)
				: null;
		const countries = version ? version.referenceCountries : [];
		const products = czReimbursement && version ? czReimbursement.products : [];
		const nonsetCountries = new Set(countries);

		const czechProducts: ProductRow[] = [];
		const suklReferencedProducts: ProductRow[] = [];
		const otherProducts: ProductRow[] = [];
		let alternativeProductsCount = 0;

		products.forEach((product) => {
			const group = resolveProductGroup(product.product, countries ?? []);
			const isAlternative = isSuklReferenceProduct(product);
			alternativeProductsCount += isAlternative || group.type === GroupTypes.other ? 1 : 0;

			if (!showAll && isAlternative) {
				return;
			}
			if (group.type === GroupTypes.czech) {
				czechProducts.push({ country: null, product });
				return;
			}
			if (group.type === GroupTypes.referenced && group.country) {
				suklReferencedProducts.push({ country: group.country, product });
				nonsetCountries.delete(group.country);
				return;
			}
			if (showAll) {
				otherProducts.push({ country: null, product });
			}
		});

		if (czechProducts.length === 0) {
			czechProducts.push({
				country: new Country('CZ', 'Czech Republic'),
				product: null,
			});
		}

		countries?.forEach((country) => {
			if (nonsetCountries.has(country)) {
				suklReferencedProducts.push({ country, product: null });
			}
		});

		suklReferencedProducts.sort((reference, otherReference) => {
			if (reference.product && !otherReference.product) {
				return -1;
			}
			if (!reference.product && otherReference.product) {
				return 1;
			}

			const comparison = reference.country?.alpha2.localeCompare(otherReference.country?.alpha2 || '') || 0;
			if (comparison !== 0) {
				return comparison;
			}

			if (reference.product && reference.product.product && otherReference.product && otherReference.product.product) {
				return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
			}
			return 0;
		});

		const pricedProducts: PricedProduct<ProductGroupCzReimbursementProduct>[] = [
			...czechProducts,
			...suklReferencedProducts,
			...otherProducts,
		]
			.filter(
				(product): product is ProductRow & { product: ProductGroupCzReimbursementProduct } => product.product !== null
			)
			.map((product) => new PricedProduct(product.product, product.product?.product, null));

		return {
			pricedProducts,
			czechProducts,
			suklReferencedProducts,
			otherProducts,
			alternativeProductsCount,
		};
	}
);

function mapStateToProps(state: State, { group }: { group: UserProductGroup }) {
	const czReimbursementState = selectCzReimbursement(state.czReimbursement, group.id);
	const czReimbursement = czReimbursementState?.czReimbursement || null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.czReimbursement
	);

	return {
		...groupProducts(czReimbursement, versions, state.products.showAllProducts),
		czReimbursement,
		validationResult: selectExport(state.czReimbursement, group.id)?.validationResult || null,
		isFetching: Boolean(czReimbursementState?.isFetching),
	};
}

type Dispatch = ThunkDispatch<State, never, Action>;

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions());

			try {
				await dispatch(commitCzReimbursementUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}

			try {
				await dispatch(fetchCzReimbursement(group.id));
			} catch (error) {
				if (!(error instanceof ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(commitCzReimbursementUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneCzReimbursement(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onUpdate:
		(
			group: UserProductGroup,
			packageSize: number | null,
			dosageStrength: number | null,
			usualTherapeuticDailyDose: number | null,
			usualTherapeuticDailyDosePerPackage: number | null,
			products: ProductUpdate[]
		): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(
					scheduleCzReimbursementUpdate(
						group.id,
						packageSize,
						dosageStrength,
						usualTherapeuticDailyDose,
						usualTherapeuticDailyDosePerPackage,
						products
					)
				);
			} catch (error) {
				dispatch(handleCzReimbursementUpdateResponse(error, group.id));
			}
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(commitCzReimbursementUpdate(group.id));
			} catch (error) {
				dispatch(handleCzReimbursementUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) => async (dispatch: Dispatch) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(CzReimbursement);
